import { StateCreator } from "zustand";
import { State, SubscriptionTier } from "./types";
import { initialState } from "./utils";
import { supabase, setIntentionalSignOut } from "../../../../lib/supabase";
import { useCentralStore } from ".";
import { Logger } from "../../../../lib/logger/Logger";
import {
	GuarantorEntityType,
	BankAccountEntityType,
	SupabaseTableEnum,
	OrganizationKeys,
	SupabaseReadTableEnum,
	SupabaseWriteTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { useDesktopsStore } from "../Desktops";
import { showNotification } from "./selectors";
import { useTemplatesStore } from "../Templates/templates.store";
import { useUserManagementStore } from "../UserManagement";
import { useConnectStore } from "../Connect";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useJobStore } from "../Jobs";
import { produce } from "immer";
import { useCatalogsStore } from "../Catalogs";
import { ExtendedArticleEntityType } from "../Jobs/types";
import { useTabConfigStore } from "../TabConfig/tab-config.store";
import { useAttributesStore } from "../Attributes";
import { useServiceCatalogStore } from "../ServiceCatalog";
import { usePatientStore } from "../Patient";
import { ProfileEntityType } from "@/lib/supabase/supabaseTypes";
import {
	getFormTemplates,
	cloneFormTemplate,
} from "../../pages/form-builder/constants/form-templates";
import { Json } from "@/lib/supabase/supabaseTypesImport";

export interface CentralSlice extends State {
	getSession: () => Promise<boolean>;
	// Get profile and get organization are separated to be able to fetch them separately
	getProfile: () => Promise<void>;
	initialize: () => Promise<void>;
	initCourier: () => Promise<void>;
	updateSearchConfig: (search_config_job_list: {
		statuses: Record<JobStatusEnum, number>;
	}) => Promise<void>;
	getOrganization: () => Promise<void>;
	getArticles: () => Promise<void>;
	getGuarantorLookup: () => Promise<void>;
	getBankAccounts: () => Promise<void>;
	handleSignOut: () => Promise<void>;
	setMFADialog: (open: boolean) => void;
	verifyMFA: () => Promise<{
		mfaVerified: boolean;
		mfaAuthenticated: boolean;
		mfaEnrolled: boolean;
	}>;
	setupMFA: (verifyCode: string, factorId: string) => Promise<void>;
	updatePrivacy: (privacy: boolean) => Promise<void>;
	deleteArticle: (articleId: number) => Promise<void>;
	duplicateArticle: (
		article: ExtendedArticleEntityType,
		newCodeE: string
	) => Promise<{ id: number } | null>;
	updateOrganization: (
		field: OrganizationKeys,
		value: any,
		noNotification?: boolean
	) => Promise<{ data: any; error: any }>;
	updateOrganizationBatch: (
		updates: Partial<Record<OrganizationKeys, any>>,
		noNotification?: boolean
	) => Promise<{ data: any; error: any }>;
	updateProfileColors: (
		profileColors: Record<string, string>
	) => Promise<void>;
	updateOrganizationConfig: (
		configKey: string,
		configValue: any
	) => Promise<void>;

	updateDefaultRegion: (regionId: number | null) => Promise<void>;
	setUploadStatus: (
		status: "idle" | "uploading" | "complete" | "error"
	) => void;
	setNeedsPasswordSetup: (value: boolean) => void;
}

async function ensureDefaultForm(orgId: string) {
	try {
		const { count, error } = await supabase
			.from(SupabaseTableEnum.FORMS)
			.select("id", { count: "exact", head: true })
			.eq("organization_id", orgId);

		if (error || (count ?? 0) > 0) return;

		const standardTemplate = getFormTemplates()[0];
		const cloned = cloneFormTemplate(standardTemplate, orgId);
		const formToInsert = {
			...cloned,
			definition: JSON.parse(JSON.stringify(cloned.definition)) as Json,
			rules: JSON.parse(JSON.stringify(cloned.rules)) as Json[],
			published_at: new Date().toISOString(),
		};

		await supabase.from(SupabaseTableEnum.FORMS).insert(formToInsert);
	} catch (e) {
		Logger.error(e, {}, "ensureDefaultForm failed silently");
	}
}

export const createCentralStore: StateCreator<CentralSlice> = (set, get) => ({
	...initialState,
	initialize: async () => {
		const {
			initialized,
			getSession,
			getArticles,
			getClients,
			getProfile,
			getOrganization,
			getSubscription,
			getGuarantorLookup,
			fetchGridConfig,
			verifyMFA,
			getBankAccounts,
			loadRegions,
			loadPriceLists,
		} = useCentralStore.getState();

		// If the initialize function is already run before
		// prevent it from running again and causing rerenders
		if (initialized) {
			Logger.warn("Central store already initialized");
			return;
		}

		set({
			allDataInitialized: false,
			appInitializedAt: new Date().toISOString(),
		});

		const loggedIn = await getSession();

		if (!loggedIn) {
			Logger.warn("[Sign in] User is not logged in");
			return;
		}

		if (get().role === 100) {
			set({ initialized: true, allDataInitialized: true });
			return;
		}

		const { mfaAuthenticated } = await verifyMFA();

		if (!mfaAuthenticated) {
			Logger.error("MFA not authenticated");
			return;
		}
		Logger.log("[Sign in] User is logged in", loggedIn);

		await getProfile();
		await getOrganization();

		const { userId, organization, role } = get();
		if (userId && organization?.id) {
			const {
				initializeAuth,
				setOnDeliveryChange,
				setOnJobRequestReceived,
				setGetNotificationNavigationPath,
			} = useRealtimeStore.getState();
			initializeAuth(userId, organization, role);
			setOnDeliveryChange((payload) => {
				useJobStore.getState().handleDeliveryRealtimeEvent(payload);
			});
			setOnJobRequestReceived(() => {
				const client = useCentralStore.getState().client;
				useJobStore
					.getState()
					.fetchJobRequestChannels(client?.connected_organization_id);
			});

			// The realtime channel is shared between dentlab and dentconnect
			// So we want to make it aware of how it should navigate from notifications for our app context
			setGetNotificationNavigationPath((channel) => {
				const myOrgId = organization.id;
				const isVendor = channel.vendor_org_id === myOrgId;

				if (isVendor) {
					const clientId =
						channel.job_client_id ||
						Object.values(
							useCentralStore.getState().clientsLookup
						).find(
							(c) =>
								c.connected_organization_id ===
								channel.customer_org_id
						)?.id;
					if (!clientId) return `/chats/${channel.id}`;

					if (channel.channel_type === "job" && channel.job_id) {
						return `/client/${clientId}/job/${channel.job_id}`;
					}
					if (
						channel.channel_type === "job_request" ||
						channel.channel_type === "job_request_denied"
					) {
						return `/client/${clientId}/job/request/${channel.id}`;
					}
					return `/client/${clientId}/general-chat`;
				} else {
					if (channel.channel_type === "job" && channel.job_id) {
						return `/vendor/${channel.vendor_org_id}/job/${channel.job_id}`;
					}
					// This case should not actually appear but let's cover all the channel types
					if (
						channel.channel_type === "job_request" ||
						channel.channel_type === "job_request_denied"
					) {
						return `/vendor/${channel.vendor_org_id}/job/request/${channel.id}`;
					}
					return `/vendor/${channel.vendor_org_id}/chat`;
				}
			});
		}
		await Promise.all([
			loadRegions(),
			getBankAccounts(),
			getClients(),
			getGuarantorLookup(),
			fetchGridConfig(),
			getSubscription(),
			useRealtimeStore.getState().getProfilesForChat(),
			userId && organization?.id
				? useAttributesStore
						.getState()
						.loadAttributes(userId, organization.id)
				: Promise.resolve(),
		]);

		set({ initialized: true });

		await Promise.all([
			getArticles(),
			loadPriceLists(),
			useCatalogsStore.getState().initialize(),
			useTemplatesStore.getState().fetchTemplates(),
			useDesktopsStore.getState().fetchDesktops(organization?.id ?? ""),
			useDesktopsStore.getState().fetchLocations(organization?.id ?? ""),
			useDesktopsStore
				.getState()
				.fetchPrinterConfigs(organization?.id ?? ""),
			useDesktopsStore.getState().fetchPrintJobs(organization?.id ?? ""),
			useConnectStore
				.getState()
				.fetchConnectedVendors(organization?.id ?? ""),
			useConnectStore.getState().fetchBrands(organization?.id ?? ""),
			useUserManagementStore.getState().getMembers(),
			useTabConfigStore
				.getState()
				.fetchConfigurations(organization?.id ?? ""),
		]);

		useServiceCatalogStore.getState().loadCatalog();

		set({
			allDataInitialized: true,
		});

		useRealtimeStore.getState().fetchLatestChannels();
		ensureDefaultForm(organization?.id ?? "");
	},
	initCourier: async () => {
		const { initialized, getSession, verifyMFA } =
			useCentralStore.getState();

		if (initialized) return;

		set({
			allDataInitialized: false,
			appInitializedAt: new Date().toISOString(),
		});

		const loggedIn = await getSession();
		if (!loggedIn) return;

		const { mfaAuthenticated } = await verifyMFA();
		if (!mfaAuthenticated) return;

		const { data, error } = await supabase.rpc("get_courier_profile");
		if (!error && data && data.length > 0) {
			const row = data[0];
			set({
				profile: {
					id: row.id,
					first_name: row.first_name,
					last_name: row.last_name,
					email: row.email,
					avatar_url: row.avatar_url,
				} as any,
				organization: { name: row.organization_name } as any,
			});
		}

		const { setOnDeliveryChange, subscribe } = useRealtimeStore.getState();
		setOnDeliveryChange(() => {
			useJobStore.getState().fetchCourierDeliveries();
		});
		subscribe();

		set({ initialized: true, allDataInitialized: true });
	},
	getSession: async () => {
		Logger.log("[Sign in] Retrieving session");
		try {
			const { data } = await supabase.auth.getSession();

			if (!data || !data.session) {
				Logger.error("No session found");
				return false;
			}

			const { user: userInformation } = data.session;
			const {
				id: userId,
				email: userEmail,
				user_metadata: userMetaData,
				app_metadata: appMetaData,
			} = userInformation;
			const { is_connect_user: isConnectUser, hasPassword } =
				userMetaData;
			const { role } = appMetaData;

			set({
				userId,
				userEmail,
				isConnectUser: isConnectUser || false,
				role,
				needsPasswordSetup: hasPassword !== true,
			});

			return true;
		} catch (error) {
			Logger.error(error, {}, "Error retrieving session");
			return false;
		}
	},
	getProfile: async () => {
		Logger.log("[Sign in] Getting profile");
		const { userId } = get();
		if (!userId) {
			Logger.error("[Sign in] User ID is not set");
			return;
		}
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.select("*")
			.eq("id", userId);

		if (error) {
			Logger.error(error);
			await get().handleSignOut();
			return;
		}

		if (data.length === 0) {
			Logger.error("[Sign in] No profile found");
			await get().handleSignOut();
			return;
		}

		const profile = data[0] as ProfileEntityType;

		if (
			profile.deactivated_at &&
			new Date(profile.deactivated_at) < new Date()
		) {
			Logger.warn("[Sign in] User account is deactivated", { userId });
			localStorage.setItem(
				"deactivation_message",
				"Ihr Konto wurde deaktiviert. Bitte kontaktieren Sie Ihren Administrator."
			);
			await get().handleSignOut();
			return;
		}

		Logger.log("[Sign in] Profile", profile);
		set({
			profile,
		});
		if (profile?.search_config_job_list) {
			set({
				searchConfig: profile.search_config_job_list as {
					statuses: Record<JobStatusEnum, number>;
				},
			});
		}
	},
	getOrganization: async () => {
		const profile = get().profile;
		if (!profile || !profile.organization_id) return;

		const { data, error } = await supabase
			.from(SupabaseReadTableEnum.ORGANIZATIONS_VIEW)
			.select()
			.eq("id", profile?.organization_id)
			.single();

		if (error) {
			Logger.error(error);
			return;
		}
		if (data) {
			set({
				organization: data,
				subscriptionTier:
					(data.subscription_tier as SubscriptionTier) ?? null,
			});
		}
	},
	updateSearchConfig: async (searchConfigJobList: {
		statuses: Record<JobStatusEnum, number>;
	}) => {
		const userId = useCentralStore.getState().userId;
		if (!userId) return Logger.error("Can't update search config");

		const { error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.update({
				search_config_job_list: searchConfigJobList,
			})
			.eq("id", userId);

		if (error) {
			Logger.error("Can't update search config");
			return;
		}

		set({
			searchConfig: searchConfigJobList,
		});
	},
	getArticles: async () => {
		const organizationId = get().organization?.id;

		if (!organizationId) {
			Logger.error("Organization id is not set");
			return;
		}

		let length = 1000;
		let lastCodeE = "";
		let userData = [];

		// First fetch without filter
		const { data: firstBatch, error: firstBatchError } = await supabase
			.from(SupabaseTableEnum.ARTICLES)
			.select("*")
			.eq("organization_id", organizationId)
			.order("code_e", { ascending: true })
			.limit(1000);

		if (firstBatchError) {
			Logger.error(firstBatchError);
			return;
		}

		userData = [...firstBatch];
		length = firstBatch.length;

		while (length === 1000 && userData.length > 0) {
			lastCodeE = userData[userData.length - 1].code_e;

			const { data: nextBatch, error: nextBatchError } = await supabase
				.from(SupabaseTableEnum.ARTICLES)
				.select("*")
				.eq("organization_id", organizationId)
				.gt("code_e", lastCodeE)
				.order("code_e", { ascending: true })
				.limit(1000);

			if (nextBatchError) {
				Logger.error(nextBatchError);
				break;
			}

			length = nextBatch.length;
			userData.push(...nextBatch);
		}

		// Create nested lookup: code_e -> id -> article
		const articlesLookup: Record<
			string,
			Record<string, ExtendedArticleEntityType>
		> = {};

		userData.forEach((curr) => {
			const extendedArticle = {
				...curr,
				custom: true,
			};
			if (curr.code_e && curr.id) {
				if (!articlesLookup[curr.code_e]) {
					articlesLookup[curr.code_e] = {};
				}
				articlesLookup[curr.code_e][curr.id.toString()] =
					extendedArticle;
			}
		});

		set({
			articles: articlesLookup,
		});
	},
	getGuarantorLookup: async () => {
		const organizationId = get().organization?.id;

		if (!organizationId) {
			Logger.error("Organization id is not set");
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.GUARANTORS)
			.select("*")
			.eq("organization_id", organizationId)
			.order("code", { ascending: true });

		if (error) {
			Logger.error(error);
			return;
		}

		const guarantorLookup = data?.reduce(
			(
				acc: Record<string, GuarantorEntityType>,
				curr: GuarantorEntityType
			) => {
				acc[curr.id] = curr;
				return acc;
			},
			{}
		);
		set({
			guarantorLookup,
		});
	},
	getBankAccounts: async () => {
		const organizationId = get().organization?.id;
		if (!organizationId) {
			Logger.error("Organization id is not set");
			return;
		}
		const { data, error } = await supabase
			.from(SupabaseTableEnum.BANK_ACCOUNTS)
			.select("*")
			.eq("organization_id", organizationId);

		if (error) {
			Logger.error(error);
			return;
		}

		set({
			bankAccountsLookup: data.reduce(
				(
					acc: Record<string, BankAccountEntityType>,
					curr: BankAccountEntityType
				) => {
					acc[curr.id] = curr;
					return acc;
				},
				{}
			),
		});
	},
	handleSignOut: async () => {
		setIntentionalSignOut(true);
		try {
			const { error } = await supabase.auth.signOut();
			if (error) throw error;

			set(initialState);

			window.location.replace("/");
		} catch (error) {
			setIntentionalSignOut(false);
			Logger.error(error, {}, "Error signing out:");
			showNotification({
				message: "Fehler beim Abmelden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
		}
	},
	setMFADialog: (open: boolean) => {
		set({
			mfaDialogOpen: open,
		});
	},
	verifyMFA: async () => {
		const { data, error } =
			await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
		let mfaVerified = false,
			mfaAuthenticated = false,
			mfaEnrolled = false;
		if (error) {
			Logger.error(error);
			return {
				mfaAuthenticated,
				mfaEnrolled,
				mfaVerified,
			};
		}

		if (data.nextLevel === "aal2" && data.nextLevel !== data.currentLevel) {
			mfaEnrolled = true;
			mfaAuthenticated = false;
			set({
				mfaEnrolled,
				mfaAuthenticated,
			});
		} else if (
			data.nextLevel === "aal1" &&
			data.nextLevel === data.currentLevel
		) {
			mfaEnrolled = false;
			mfaAuthenticated = true;
			set({
				mfaEnrolled: false,
				mfaAuthenticated: true,
			});
		} else if (
			data.nextLevel === "aal2" &&
			data.nextLevel === data.currentLevel
		) {
			mfaEnrolled = true;
			mfaAuthenticated = true;
			set({
				mfaEnrolled: true,
				mfaAuthenticated: true,
			});
		}
		mfaVerified = true;
		set({
			mfaVerified,
		});

		return {
			mfaVerified,
			mfaAuthenticated,
			mfaEnrolled,
		};
	},
	setupMFA: async (verifyCode: string, factorId: string) => {
		const challenge = await supabase.auth.mfa.challenge({ factorId });
		if (challenge.error) {
			throw challenge.error;
		}

		const challengeId = challenge.data.id;

		const verify = await supabase.auth.mfa.verify({
			factorId,
			challengeId,
			code: verifyCode,
		});
		if (verify.error) {
			throw verify.error;
		}
		set({
			mfaEnrolled: true,
		});
		showNotification({
			message: "MFA erfolgreich eingerichtet",
			type: "success",
		});
	},
	updatePrivacy: async (privacy: boolean) => {
		const profile = get().profile;
		if (!profile) return;

		set({
			profile: {
				...profile,
				data_privacy_mode: privacy ? new Date().toISOString() : null,
			},
		});

		const { patientName } = usePatientStore.getState();
		usePatientStore.setState({
			patientName: (patient, orderLastNameFirst) =>
				patientName(patient, orderLastNameFirst),
		});

		const { error } = await supabase
			.from(SupabaseTableEnum.PROFILES)
			.update({
				data_privacy_mode: privacy ? new Date().toISOString() : null,
			})
			.eq("id", profile.id);

		if (error) {
			Logger.error(error, {}, "Error updating Privacy");
			set({
				profile,
			});
		}
	},
	deleteArticle: async (articleId: number) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.ARTICLES)
			.delete()
			.eq("id", articleId);

		if (error) {
			showNotification({
				message: "Fehler beim Löschen des Artikels",
				type: "error",
			});
			Logger.error(error, {}, "Error deleting article");
			return;
		}
		await get().getArticles();

		const catalogStore = useServiceCatalogStore.getState();
		const usage = catalogStore.checkItemUsage({ article_id: articleId });
		if (usage.length > 0) {
			catalogStore.removeItemGlobally({ article_id: articleId });
			await catalogStore.saveCatalog();
		}

		showNotification({
			message: "Artikel erfolgreich gelöscht",
			type: "success",
		});
	},
	duplicateArticle: async (article, newCodeE) => {
		const { organization } = get();
		if (!organization?.id) return null;

		const {
			custom: _custom,
			is_new: _isNew,
			id: _id,
			modified_at: _modifiedAt,
			created_at: _createdAt,
			...articleData
		} = article;

		const { data, error } = await supabase
			.from(SupabaseTableEnum.ARTICLES)
			.insert({
				...articleData,
				code_e: newCodeE,
				organization_id: organization.id,
			})
			.select()
			.single();

		if (error) {
			Logger.error(error, {}, "Error duplicating article");
			showNotification({
				message: "Artikel konnte nicht dupliziert werden",
				type: "error",
			});
			return null;
		}

		await get().getArticles();

		return { id: data.id };
	},
	updateProfileColors: async (profileColors: Record<string, string>) => {
		const organizationId = get().organization?.id;
		if (!organizationId) {
			showNotification({
				message: "Fehler beim Aktualisieren der Farben",
				type: "error",
			});
			Logger.error(
				"Organization ID not found when updating profile colors"
			);
			return;
		}

		const { error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({
				profile_colors: profileColors,
			})
			.eq("id", organizationId);

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren der Farben",
				type: "error",
			});
			Logger.error(error, {}, "Error updating profile colors");
			return;
		}

		// Update organization in state
		set(
			produce((state) => {
				state.organization.profile_colors = profileColors;
			})
		);

		showNotification({
			message: "Farben erfolgreich aktualisiert",
			type: "success",
		});
	},
	updateOrganizationConfig: async (configKey: string, configValue: any) => {
		const { organization, userId, role } = get();
		if (!organization || !organization.id || !userId) {
			showNotification({
				message: "Fehler beim Aktualisieren der Konfiguration",
				type: "error",
			});
			Logger.error("Organization not found when updating config");
			return;
		}

		// Build the new config object
		const currentConfig = (organization.config as any) || {};
		const newConfig = {
			...currentConfig,
			[configKey]: configValue,
		};

		const { error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({
				config: newConfig,
			})
			.eq("id", organization?.id);

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren der Konfiguration",
				type: "error",
			});
			Logger.error(error, {}, "Error updating organization config");
			return;
		}

		const newOrganization = {
			...organization,
			config: newConfig,
		};

		// Update organization in state
		set(
			produce((state) => {
				if (state.organization) {
					state.organization = newOrganization;
				}
			})
		);

		// This updates the organization in the realtime store used by the useStorage hook
		// to make sure the right download option is used. Could be improved
		const { initializeAuth } = useRealtimeStore.getState();
		initializeAuth(userId, newOrganization, role);

		showNotification({
			message: "Konfiguration erfolgreich aktualisiert",
			type: "success",
		});
	},
	updateOrganization: async (
		field: OrganizationKeys,
		value: any,
		noNotification?: boolean
	) => {
		const { organization } = get();
		if (!organization || !organization.id) {
			showNotification({
				message: "Fehler beim Aktualisieren der Organisation",
				type: "error",
			});
			Logger.error("Organization not found when updating organization");
			return { data: null, error: "Organization not found" };
		}

		const { data, error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({
				[field]: value,
			})
			.eq("id", organization.id)
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren der Organisation",
				type: "error",
			});
			Logger.error(error, {}, "Error updating organization");
			return { data: null, error };
		}

		if (data) {
			// Update the organization in state with the returned data
			set(
				produce((state) => {
					state.organization = { ...state.organization, ...data };
				})
			);
			if (!noNotification) {
				showNotification({
					message: "Organisation erfolgreich aktualisiert",
					type: "success",
				});
			}
		}

		return { data, error: null };
	},
	updateOrganizationBatch: async (
		updates: Partial<Record<OrganizationKeys, any>>,
		noNotification?: boolean
	) => {
		const { organization } = get();
		if (!organization?.id) {
			showNotification({
				message: "Fehler beim Aktualisieren der Organisation",
				type: "error",
			});
			Logger.error("Organization not found when updating organization");
			return { data: null, error: "Organization not found" };
		}

		const { data, error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update(updates)
			.eq("id", organization.id)
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren der Organisation",
				type: "error",
			});
			Logger.error(error, {}, "Error updating organization");
			return { data: null, error };
		}

		if (data) {
			set(
				produce((state) => {
					state.organization = { ...state.organization, ...data };
				})
			);
			if (!noNotification) {
				showNotification({
					message: "Organisation erfolgreich aktualisiert",
					type: "success",
				});
			}
		}

		return { data, error: null };
	},

	updateDefaultRegion: async (regionId: number | null) => {
		const { organization } = get();
		if (!organization?.id) {
			Logger.error("Organization not found when updating default region");
			return;
		}
		const { error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({ default_activated_region_id: regionId })
			.eq("id", organization.id);

		if (error) {
			Logger.error(error, {}, "Error updating default region");
			return;
		}

		set(
			produce((state) => {
				if (state.organization) {
					state.organization.default_activated_region_id = regionId;
				}
			})
		);

		showNotification({
			message: "Standardregion erfolgreich aktualisiert",
			type: "success",
		});
	},
	setUploadStatus: (status: "idle" | "uploading" | "complete" | "error") => {
		set({ uploadStatus: status });
	},
	setNeedsPasswordSetup: (value: boolean) => {
		set({ needsPasswordSetup: value });
	},
});
